import { Navigate } from 'react-router-dom'
import type { PermissionCheck } from './permission-values'
import { usePermissions } from './use-permissions'

interface PermissionRouteProps {
  /** Granular permission check (name + level). Evaluated via usePermissions().can. */
  check?: PermissionCheck
  /** Alternative to `check` — grants access to any Admin or SuperAdmin user. */
  requireAdmin?: boolean
  /**
   * Grants access ONLY to SuperAdmin users. Tenant Admins and any `check` are
   * denied — this is stricter than `requireAdmin`.
   */
  requireSuperAdmin?: boolean
  /** Where to send the user when access is denied. Defaults to `/dashboard`. */
  redirectTo?: string
  children: React.ReactNode
}

/**
 * Route-level guard for permission-gated pages. Renders `children` only if the
 * current user satisfies the check; otherwise redirects. Fails closed when no
 * check is supplied — the previous SPA allowed URL-level access to pages whose
 * menu link was hidden by permissions, which this guard prevents.
 */
export function PermissionRoute({
  check,
  requireAdmin,
  requireSuperAdmin,
  redirectTo = '/dashboard',
  children,
}: PermissionRouteProps) {
  const { can, isAdmin, isSuperAdmin } = usePermissions()

  const allowed = (() => {
    if (isSuperAdmin) return true
    if (requireSuperAdmin) return false
    if (requireAdmin) return isAdmin
    if (check) return can(check)
    return false
  })()

  if (!allowed) return <Navigate to={redirectTo} replace />
  return <>{children}</>
}
